import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { getToken } from '../api';
import { useI18n } from '../i18n/context';
import ListToolbar from '../ui/ListToolbar';
import { loadState, persistState, resetState } from '../utils/statePersistence';
import '../styles/ui.css';
import './ListPages.css';

export default function TimeEntries() {
  const { t, formatDateTime } = useI18n();
  const defaults = { page: 1, organizationId: '', userId: '', startDate: '', endDate: '' };
  const { state: persisted } = loadState('sa-timeentries-state', defaults, {
    page: { param: 'page', parse: (v) => Math.max(1, parseInt(v, 10) || 1), serialize: (v) => String(v) },
    organizationId: { param: 'org' },
    userId: { param: 'user' },
    startDate: { param: 'from' },
    endDate: { param: 'to' }
  });

  const [data, setData] = useState({ data: [], pagination: { page: persisted.page, limit: 50, total: 0 } });
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(persisted.page);
  const [filters, setFilters] = useState({
    organizationId: persisted.organizationId,
    userId: persisted.userId,
    startDate: persisted.startDate,
    endDate: persisted.endDate
  });
  const [exporting, setExporting] = useState(false);

  const loadOrgs = useCallback(() => {
    api('/superadmin/organizations?limit=500')
      .then((r) => setOrgs(r.data || []))
      .catch(() => setOrgs([]));
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  useEffect(() => {
    persistState(
      'sa-timeentries-state',
      { page, ...filters },
      defaults,
      {
        page: { param: 'page', serialize: (v) => String(v) },
        organizationId: { param: 'org' },
        userId: { param: 'user' },
        startDate: { param: 'from' },
        endDate: { param: 'to' }
      }
    );
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 50 });
    if (filters.organizationId) params.set('organizationId', filters.organizationId);
    if (filters.userId) params.set('userId', filters.userId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    api(`/superadmin/time-entries?${params}`)
      .then(setData)
      .catch(() => setData({ data: [], pagination: { page: 1, limit: 50, total: 0 } }))
      .finally(() => setLoading(false));
  }, [page, filters.organizationId, filters.userId, filters.startDate, filters.endDate]);

  const handleExportCsv = async () => {
    const params = new URLSearchParams({ format: 'csv', limit: 5000 });
    if (filters.organizationId) params.set('organizationId', filters.organizationId);
    if (filters.userId) params.set('userId', filters.userId);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    setExporting(true);
    try {
      const token = getToken();
      const base = window.location.origin;
      const res = await fetch(`${base}/api/superadmin/time-entries?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'time-entries.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      // ignore
    } finally {
      setExporting(false);
    }
  };

  const { data: list, pagination } = data;
  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 50)));

  function formatHours(mins) {
    if (mins == null) return '—';
    const h = (mins / 60).toFixed(1).replace(/\.0$/, '');
    return `${h} h`;
  }

  function statusLabel(status) {
    if (status === 'completed') return t('timeEntries.statusCompleted');
    if (status === 'active') return t('timeEntries.statusActive');
    return t('timeEntries.statusError');
  }

  const selectedOrgName = filters.organizationId
    ? orgs.find((o) => o.id === filters.organizationId)?.name || filters.organizationId
    : null;

  return (
    <div className="sa-page-frame">
      <div className="sa-page-header">
        <h1 className="sa-page-title">{t('timeEntries.title')}</h1>
        <p className="sa-page-subtitle">{selectedOrgName ? `${t('timeEntries.filterByOrg')}: ${selectedOrgName}` : t('timeEntries.subtitle')}</p>
      </div>
      <div className="ui-page-toolbar">
        <ListToolbar
          title={null}
          subtitle={null}
          filters={[
            <select
              key="org"
              className="ui-input"
              style={{ minWidth: '170px' }}
              value={filters.organizationId}
              onChange={(e) => { setFilters((f) => ({ ...f, organizationId: e.target.value })); setPage(1); }}
            >
              <option value="">{t('common.all')}</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>,
            <input
              key="user"
              type="text"
              className="ui-input"
              style={{ minWidth: '160px' }}
              placeholder={t('timeEntries.filterByUser')}
              value={filters.userId}
              onChange={(e) => { setFilters((f) => ({ ...f, userId: e.target.value.trim() })); setPage(1); }}
            />,
            <input
              key="from"
              type="date"
              className="ui-input"
              value={filters.startDate}
              onChange={(e) => { setFilters((f) => ({ ...f, startDate: e.target.value })); setPage(1); }}
            />,
            <input
              key="to"
              type="date"
              className="ui-input"
              value={filters.endDate}
              onChange={(e) => { setFilters((f) => ({ ...f, endDate: e.target.value })); setPage(1); }}
            />,
            <button
              key="reset"
              className="ui-button ghost"
              type="button"
              onClick={() => {
                const next = resetState('sa-timeentries-state', defaults);
                setFilters({
                  organizationId: next.organizationId,
                  userId: next.userId,
                  startDate: next.startDate,
                  endDate: next.endDate
                });
                setPage(next.page);
              }}
            >
              {t('common.reset')}
            </button>,
            <button
              key="export"
              type="button"
              className="ui-button ghost"
              onClick={handleExportCsv}
              disabled={exporting}
            >
              {exporting ? t('common.loading') : t('timeEntries.exportCsv')}
            </button>
          ]}
        />
      </div>

      <div className="ui-page-content">
        <div className="sa-card sa-table-card">
          {loading ? (
            <div className="sa-loading">{t('common.loading')}</div>
          ) : list.length === 0 ? (
            <div className="sa-empty">{t('timeEntries.empty')}</div>
          ) : (
            <>
              <div className="sa-table-wrap">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>{t('timeEntries.user')}</th>
                      <th>{t('timeEntries.organization')}</th>
                      <th>{t('timeEntries.clockIn')}</th>
                      <th>{t('timeEntries.clockOut')}</th>
                      <th>{t('timeEntries.hours')}</th>
                      <th>{t('timeEntries.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((row) => (
                      <tr key={row.id}>
                        <td><strong>{row.userName}</strong><br /><span className="sa-table-muted">{row.userEmail}</span></td>
                        <td>{row.organizationName}</td>
                        <td>{row.clockIn ? formatDateTime(row.clockIn) : '—'}</td>
                        <td>{row.clockOut ? formatDateTime(row.clockOut) : '—'}</td>
                        <td>{formatHours(row.workedMinutes)}</td>
                        <td>
                          <span className={`sa-badge sa-badge--${row.status === 'completed' ? 'success' : row.status === 'active' ? 'info' : 'error'}`}>
                            {statusLabel(row.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="sa-pagination">
                  <button type="button" className="sa-btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('common.previous')}</button>
                  <span className="sa-pagination-info">{t('common.page')} {page} {t('common.of')} {totalPages} · {pagination.total} {t('common.results')}</span>
                  <button type="button" className="sa-btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t('common.next')}</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
